<template>

  <div class="report-container" v-loading="loading" element-loading-text="Loading report...">
    <!-- Showing the reduced fallback report because the primary one is premium-only for
         this viewer's resource — let them know a fuller version exists. -->
    <div v-if="!loading && usingFallback" class="upgrade-banner">
      <i class="fas fa-crown upgrade-banner-icon"></i>
      <span class="upgrade-banner-text">{{ upgradeNoteText || $t('ReportBuilder.messages.upgradeBannerDefault') }}</span>
      <router-link to="/tariff" class="upgrade-banner-link">{{ $t('PremiumOnly.button') }}</router-link>
    </div>
    <!-- Report is gated behind a tariff the viewer's selected resource doesn't have -->
    <div v-if="!loading && premiumRequired" class="report-config-error">
      <i class="fas fa-crown report-config-error-icon premium-icon"></i>
      <div class="report-config-error-title">{{ $t('ReportBuilder.messages.premiumRequiredTitle') }}</div>
      <p class="report-config-error-list">{{ $t('ReportBuilder.messages.premiumRequiredText') }}</p>
      <router-link to="/tariff" class="btn btn-sm btn-premium-inline mt-2">{{ $t('PremiumOnly.button') }}</router-link>
    </div>
    <!-- Template report whose :params (e.g. refund event) aren't provided for this context -->
    <div v-else-if="!loading && missingParams.length" class="report-config-error">
      <i class="fas fa-sliders report-config-error-icon"></i>
      <div class="report-config-error-title">{{ $t('ReportBuilder.messages.missingParamsTitle') }}</div>
      <ul class="report-config-error-list">
        <li v-for="p in missingParams" :key="p">{{ p }}</li>
      </ul>
    </div>
    <div v-else-if="!loading && hasConfigError" class="report-config-error">
      <i class="fas fa-exclamation-triangle report-config-error-icon"></i>
      <div class="report-config-error-title">{{ $t('ReportBuilder.messages.incompleteConfig') }}</div>
      <ul class="report-config-error-list">
        <li v-for="(issue, idx) in configIssues" :key="idx">{{ issueMessage(issue) }}</li>
      </ul>
    </div>
    <div v-else-if="!loading || reportInfo || everFinishedLoading" class="h-100 d-flex flex-column">
      <!--  manage -->
      <!--  chart -->
      <Chart v-if="computedReportData.type_chart !== 'list' && computedReportData.type_chart !== 'table' && computedReportData.type_chart !== 'funnels' && computedReportData.type_chart !== 'utm_table' && computedReportData.type_chart !== 'paths'" :hide-controls="hideControls" class="flex-grow-1 report-panel" />
      <FunnelsChart v-if="computedReportData.type_chart === 'funnels'" :hide-controls="hideControls" :hide-detailed-data="hideDetailedData" :hide-steps-data="hideStepsData" class="flex-grow-1 report-panel" />
      <PathsChart v-if="computedReportData.type_chart === 'paths'" class="flex-grow-1 report-panel" />
      <!-- SQL: строки ответа как есть — самостоятельный вид (type_chart: table).
           Под графиком детализация общая с трендами, см. showTrendsTable -->
      <SqlTable v-if="isSqlReport && computedReportData.type_chart === 'table'" :hide-controls="hideControls" class="flex-grow-1 report-panel" />
      <TrendsTable v-if="showTrendsTable" class="flex-grow-1 report-panel" />
      <TableData v-if="computedReportData.type_chart === 'list'" hideDate class="flex-grow-1 report-panel" />
      <RetentionTable v-if="!hideDetailedData && computedReportData.type_chart === 'retention'" class="flex-grow-1 report-panel" />
      <UtmReportView v-if="computedReportData.type_chart === 'utm_table' || isUtmTrendsBreakdown(computedReportData)" class="flex-grow-1 report-panel" />
      <!--  table -->
    </div>
    <div v-else-if="loading && !reportInfo && !everFinishedLoading" class="loading-placeholder">
    </div>
  </div>

</template>

<script>
import Chart from "@/components/reports/_chart.vue";
import FunnelsChart from "@/components/reports/_funnels_chart.vue";
import PathsChart from "@/components/reports/_paths_chart.vue";
import TrendsTable from "@/views/report_builder/builder/_trends_table.vue";
import TableData from "@/views/report_builder/builder/_table_data.vue";
import RetentionTable from "@/views/report_builder/builder/_retention_table.vue";
import UtmReportView from "@/views/report_builder/builder/_utm_report.vue";
import SqlTable from "@/views/report_builder/builder/_sql_table.vue";
import userReportService from "@/helpers/api/userReport.service";

import { getConfigIssues } from "@/helpers/reportValidation";
import { applySqlResult, buildSqlChartData } from "@/helpers/reports/sqlChartAdapter";
import {
  buildReportPayload,
  fetchReportData,
  fetchSqlReportData,
  resolveResourceParams,
  unwrapReportData
} from "@/helpers/reports/executeReport";
import { normalizeCompare, buildComparePayload, isCompareSupported, extractCompareAxis } from "@/helpers/reports/compare";
import { isUtmTrendsBreakdown } from "@/helpers/reports/utmTrends";
import { buildFiltersWithCohort } from "@/helpers/reports/cohortFilter";
import { reactive, computed } from 'vue';

export default {
  name: "Report",
  components: {Chart, FunnelsChart, PathsChart, TableData, TrendsTable, RetentionTable, UtmReportView, SqlTable},
  data: function() {
    return {
      localLoading: true,
      reportInfo: null,
      missingParams: [],
      premiumRequired: false,
      // Whichever report is actually being rendered right now — report_id normally,
      // fallbackReportId once we've degraded to it. loadCompareData follows this,
      // not report_id, so the comparison period stays consistent with what's on screen.
      activeReportId: null,
      everFinishedLoading: false,
      // Local state to avoid global Vuex collisions in dashboards
      localReportState: reactive({
        type: 'trends',
        source_request: {},
        query: {},
        chartData: null,
        type_chart: 'line',
        // The report's own type_chart before any widget override — lets us restore it
        // client-side when the dashboard widget's override is cleared, without a reload
        // (see applyWidgetTypeChartOverride; overriding never changes the underlying
        // query, all trend chart types share TrendsQueryService server-side).
        baseTypeChart: 'line',
        visual: {},
        settings: {
          params: {
            ':date_from': '-30d',
            ':date_to': 'now',
            ':resource_key': []
          }
        },
        total_count: 0,
        hiddenDatasets: [],
        // Сравнение периодов: результат второго запроса. Конфиг (что с чем сравниваем)
        // приходит внутри visual.compare вместе с сохранённым отчётом.
        compareData: null,
        comparePeriod: null,
        compareLoading: false,
        // SQL-режим: сырые строки сравнительного периода (для дельт в таблице результата)
        compareSqlRows: [],
        utmActiveMetric: 0,
        utmExpandedRows: {},
        // SQL-отчёты: строки ответа и их колонки (см. helpers/reports/sqlChartAdapter)
        sqlRows: [],
        sqlColumns: [],
        sqlErrors: []
      })
    }
  },
  provide() {
    return {
      // Provide local state to all child components if isolated
      injectedReportData: this.isolated ? this.localReportState : null,
      // Isolated children (UTM drill-down, funnel step users) must build their
      // ad-hoc requests from THIS widget's config, not from the global Vuex
      // builder state — in a dashboard that state belongs to another report
      // (or is empty), which produced default `line`/no-event requests.
      injectedBuildPayload: this.isolated ? () => this.buildExecutePayload() : null,
      // Series style controls (color picker, line settings) only make sense in the
      // constructor — everywhere else Report is rendered read-only (dashboard, report
      // view page, AI assistant, referral sparklines)
      injectedEditable: this.editable,
      // Dashboard template params (e.g. { ':payment_event_id': [123] }) — chart/table/funnel
      // labels need this to resolve a widget's `:param` event_id placeholder back to the
      // real event id before looking up its name, since the saved widget template only
      // ever holds the unresolved placeholder string (substitution happens server-side).
      injectedExtraParams: computed(() => this.extraParams)
    }
  },
  props: {
    isolated: {
      type: Boolean,
      default: false
    },
    // True only in the report constructor (Builder.vue) — lets detail tables show
    // per-series style controls (color picker, line settings). Everywhere else Report
    // is a read-only view, so those controls stay hidden.
    editable: {
      type: Boolean,
      default: false
    },
    reportData: {
      type: Object,
      default: null
    },
    report_id: {
      type: String,
      default: null
    },
    hideControls: {
      type: Boolean,
      default: false
    },
    globalDateRange: {
      type: Object,
      default: () => ({ from: null, to: null })
    },
    globalGranularity: {
      type: String,
      default: null
    },
    // Сравнение периодов, заданное на уровне дашборда. Перекрывает visual.compare
    // самого отчёта, чтобы все виджеты сравнивались одинаково.
    globalCompare: {
      type: Object,
      default: null
    },
    hideDetailedData: {
      type: Boolean,
      default: false
    },
    // Funnel-only: hides the "completed N steps" info block under the chart, independent
    // of hideDetailedData which hides the breakdown table below it.
    hideStepsData: {
      type: Boolean,
      default: false
    },
    // Extra settings.params merged into every execute call, e.g. a system dashboard's
    // resolved template params: { ':payment_event_id': [123] }. Keys must include the ':'.
    extraParams: {
      type: Object,
      default: null
    },
    // Reduced-version report shown instead of the premium stub when the primary report
    // (report_id) is tariff-gated for the viewer's selected resource — lets a dashboard
    // widget degrade gracefully for free-tier viewers instead of just refusing to render.
    fallbackReportId: {
      type: String,
      default: null
    },
    // Already locale-resolved custom blurb explaining the paid/free difference (see
    // WidgetWrapper's per-widget settings); falls back to a generic banner text if empty.
    upgradeNoteText: {
      type: String,
      default: null
    },
    // Per-widget chart display overrides (legend/dates/axis scale/grid) from the
    // dashboard's own widget.settings — merged on top of the report's own saved
    // `visual`, same pattern as globalCompare. Opening the report standalone doesn't
    // pass this prop, so it keeps its own stock visual settings.
    widgetVisualOverride: {
      type: Object,
      default: null
    },
    // Per-widget trend display-type override (line/bar/number/...) — all trend chart
    // types share the same backend query (TrendsQueryService), so this is a pure
    // render-time swap of type_chart, same pattern as widgetVisualOverride.
    widgetTypeChartOverride: {
      type: String,
      default: null
    },
    // Per-widget fixed date range (a relative token like 'today'/'-7d'/'-30d') that
    // pins this widget's period regardless of the dashboard's own global date range.
    widgetDateRangeOverride: {
      type: String,
      default: null
    },
    // Dashboard-level segment (cohort) override: merged into source_request.filters as
    // an 'in_cohort' condition, replacing any cohort filter the report itself might have
    // saved but leaving every other filter condition untouched. Unlike the visual/type_chart
    // overrides this changes what gets queried, so it triggers a real reload — see the watcher.
    widgetSegmentOverride: {
      type: [Number, String],
      default: null
    }
  },
  computed: {
    computedReportData() {
      return this.isolated ? this.localReportState : this.$store.state.ReportFromBuilder;
    },
    loading() {
      return this.isolated ? this.localLoading : (this.localLoading || this.$store.state.ReportFromBuilder.loading);
    },
    isSqlReport() {
      return this.computedReportData.type === 'sql' || this.computedReportData.source_request?.type === 'sql';
    },
    // Детализация трендового вида: серии по строкам, ось X по колонкам. Не нужна там,
    // где у отчёта своя таблица (список, воронки, ретеншен, UTM, пути), и в SQL-режиме
    // с сырым табличным видом — там таблица и есть сам отчёт.
    showTrendsTable() {
      if (this.hideDetailedData) return false;
      const typeChart = this.computedReportData.type_chart;
      if (['list', 'funnels', 'retention', 'utm_table', 'paths'].includes(typeChart)) return false;
      if (isUtmTrendsBreakdown(this.computedReportData)) return false;
      return !(this.isSqlReport && typeChart === 'table');
    },
    configIssues() {
      return getConfigIssues({
        type: this.computedReportData.type,
        source_request: this.computedReportData.source_request
      });
    },
    hasConfigError() {
      return this.configIssues.length > 0;
    },
    // Меняется при переключении бота в шапке (сайдбар/мобильное меню). Большинство
    // отчётов фильтруются по $current_resource (см. resolveResourceParams), так что
    // без перезапроса дашборд/страница отчёта продолжали бы показывать данные старого бота.
    currentBotKey() {
      return this.$store.state.init?.current_bot?.key || null;
    },
    // True once we've actually degraded to the reduced report (not while the primary
    // is still being tried, and not when it's blocked with no fallback configured).
    usingFallback() {
      return !!this.fallbackReportId && !this.premiumRequired && this.activeReportId === this.fallbackReportId;
    }
  },
  watch: {
    loading(val) {
      if (!val) this.everFinishedLoading = true;
    },
    currentBotKey() {
      this.loadReport();
    },
    globalDateRange: {
      deep: true,
      handler(newVal) {
        // A pinned widget date range takes precedence over the dashboard's global one
        if (this.widgetDateRangeOverride) return;
        if (newVal && (newVal.from || newVal.to)) {
          this.localReportState.settings.params[':date_from'] = newVal.from;
          this.localReportState.settings.params[':date_to'] = newVal.to;
          this.loadReport();
        }
      }
    },
    // Edited live in dashboard edit mode — a real reload since the date range changes
    // what gets queried (unlike the type_chart override, which is render-only).
    widgetDateRangeOverride() {
      this.loadReport();
    },
    globalGranularity(newVal) {
      if (newVal) {
        this.localReportState.source_request.granularity = newVal;
        this.loadReport();
      }
    },
    globalCompare: {
      deep: true,
      handler() {
        this.applyGlobalCompare();
        this.loadReport();
      }
    },
    // Widget settings are edited live (dashboard edit mode) — re-merge into visual
    // without a full reload, same as chart-type/legend toggles in the builder itself.
    widgetVisualOverride: {
      deep: true,
      handler() {
        this.applyWidgetVisualOverride();
      }
    },
    widgetTypeChartOverride() {
      this.applyWidgetTypeChartOverride();
    },
    widgetSegmentOverride() {
      this.loadReport();
    },
    extraParams: {
      deep: true,
      handler() {
        this.loadReport();
      }
    }
  },
  methods: {
    isUtmTrendsBreakdown,
    issueMessage(issue) {
      const stepName = (i) => issue.label || String.fromCharCode(65 + (i ?? 0));
      switch (issue.code) {
        case 'stepNoEvent':
          return this.$t('ReportBuilder.messages.issueStepNoEvent', { step: stepName(issue.index) });
        case 'stepFilters':
          return this.$t('ReportBuilder.messages.issueStepFilters', { step: stepName(issue.index) });
        case 'stepMissingAgrField':
          return this.$t('ReportBuilder.messages.issueStepMissingAgrField', { step: stepName(issue.index) });
        case 'funnelNoSteps':
          return this.$t('ReportBuilder.messages.issueFunnelNoSteps');
        case 'utmNoRequests':
        case 'utmNoEvent':
          return this.$t('ReportBuilder.messages.issueUtmNoEvent');
        case 'retentionTargetFilters':
          return this.$t('ReportBuilder.messages.issueRetentionTargetFilters');
        case 'retentionReturningFilters':
          return this.$t('ReportBuilder.messages.issueRetentionReturningFilters');
        case 'topLevelFilters':
          return this.$t('ReportBuilder.messages.issueTopLevelFilters');
        case 'sqlEmpty':
          return this.$t('ReportBuilder.messages.issueSqlEmpty');
        default:
          return this.$t('ReportBuilder.messages.issueUnknown');
      }
    },
    syncToLocalState(payload) {
      if (!payload) return;
      // The top-level `type`/`type_chart` envelope is optional — the engine reads only
      // `source_request`. Fall back to it so a config saved without the envelope (API/MCP/AI)
      // still renders with its real chart type instead of the default 'line'.
      const sr = payload.source_request;
      if (payload.type) this.localReportState.type = payload.type;
      else if (sr?.type) this.localReportState.type = sr.type;
      if (payload.source_request) this.localReportState.source_request = JSON.parse(JSON.stringify(payload.source_request));
      if (payload.query) this.localReportState.query = payload.query;
      if (payload.type_chart) this.localReportState.type_chart = payload.type_chart;
      else if (sr?.type_chart) this.localReportState.type_chart = sr.type_chart;
      this.localReportState.baseTypeChart = this.localReportState.type_chart;
      if (payload.visual) this.localReportState.visual = payload.visual;
      if (payload.settings) this.localReportState.settings = JSON.parse(JSON.stringify(payload.settings));
      if (payload.total_count !== undefined) this.localReportState.total_count = payload.total_count;
      this.localReportState.chartData = null;
      this.localReportState.compareData = null;
      this.localReportState.comparePeriod = null;
      this.localReportState.compareSqlRows = [];
      this.localReportState.hiddenDatasets = [];
      this.localReportState.utmExpandedRows = {};
    },
    /**
     * Записывает настройку сравнения уровня дашборда прямо в visual локального состояния.
     * Не «подменяем на лету» в loadCompareData, потому что график и таблицы детализации
     * читают именно visual.compare — иначе запрос ушёл бы, а отрисовки не случилось.
     */
    applyGlobalCompare() {
      if (!this.globalCompare) return;
      this.localReportState.visual = {
        ...(this.localReportState.visual || {}),
        compare: JSON.parse(JSON.stringify(this.globalCompare))
      };
    },
    // Same idea as applyGlobalCompare, for the dashboard widget's own legend/dates/
    // axis-scale/grid overrides — patched onto the report's saved `visual` so the
    // saved report_config (and the standalone report page) stay untouched.
    applyWidgetVisualOverride() {
      if (!this.widgetVisualOverride) return;
      this.localReportState.visual = {
        ...(this.localReportState.visual || {}),
        ...JSON.parse(JSON.stringify(this.widgetVisualOverride))
      };
    },
    // Swaps type_chart for the widget's own choice, falling back to the report's real
    // saved type_chart (baseTypeChart) once the override is cleared — no reload needed,
    // every trend chart type resolves to the same backend query.
    applyWidgetTypeChartOverride() {
      this.localReportState.type_chart = this.widgetTypeChartOverride || this.localReportState.baseTypeChart;
    },
    // Dashboard-level "Сегменты" picker: patches the widget's own source_request.filters
    // right after syncToLocalState() reset it to the report's saved config, same slot as
    // applyGlobalCompare(). Nothing to do (and nothing to undo) when cleared — the next
    // loadReport() call re-syncs from the pristine template before this runs again.
    applySegmentOverride() {
      if (!this.widgetSegmentOverride) return;
      if (!this.localReportState.source_request) this.localReportState.source_request = {};
      this.localReportState.source_request.filters = buildFiltersWithCohort(
        this.localReportState.source_request.filters,
        this.widgetSegmentOverride
      );
    },
    // settings.params with extraParams merged in and $current_resource resolved
    buildResolvedSettings() {
      const resolvedSettings = JSON.parse(JSON.stringify(this.localReportState.settings || {}));
      if (this.extraParams && Object.keys(this.extraParams).length) {
          if (!resolvedSettings.params) resolvedSettings.params = {};
          Object.assign(resolvedSettings.params, JSON.parse(JSON.stringify(this.extraParams)));
      }
      return resolveResourceParams(resolvedSettings, this.$store.state.init?.current_bot?.key);
    },
    // Full backend payload for this widget's config — used by loadReport and,
    // through `injectedBuildPayload`, by children that fire their own requests.
    buildExecutePayload() {
      return buildReportPayload(this.localReportState, {
        extraParams: this.extraParams,
        currentBotKey: this.$store.state.init?.current_bot?.key
      });
    },
    /**
     * Второй запрос того же отчёта с патчем поверх базового payload — период сравнения
     * (в перспективе — сегменты/когорты через visual.compare.overrides).
     * Считается от УЖЕ подставленных дат, поэтому в дашборде следует за глобальным периодом.
     */
    async loadCompareData(basePayload) {
      // A newer compare load (or component teardown) supersedes whatever's in flight —
      // abort it so a stale response can't overwrite fresher state, and so a dashboard
      // switch away from this widget doesn't leave its request occupying a connection.
      if (this._compareAbortController) this._compareAbortController.abort();
      const controller = new AbortController();
      this._compareAbortController = controller;
      const signal = controller.signal;

      const state = this.localReportState;
      const compare = normalizeCompare(state.visual);
      const clear = () => {
        state.compareData = null;
        state.comparePeriod = null;
        state.compareSqlRows = [];
      };

      if (!compare || !isCompareSupported(state.type, state.type_chart)) {
        clear();
        return;
      }

      // Ось основного ответа — источник правды для сдвига периода (у SQL ось X
      // произвольная, не обязательно датная, поэтому там её не берём)
      const axis = extractCompareAxis(state, state.chartData);
      const built = buildComparePayload(basePayload, compare, axis);
      if (!built) {
        clear();
        return;
      }

      state.compareLoading = true;
      try {
        if (this.isSqlReport) {
          // Строки сравнения ложатся в ТЕКУЩИЙ маппинг: он уже подобран основным
          // результатом, переподбор развёл бы два периода по разным колонкам
          const response = await fetchSqlReportData(built.payload, { reportUuid: this.activeReportId || null, signal });
          state.compareSqlRows = unwrapReportData(response).rows;
          state.compareData = buildSqlChartData(
            state.compareSqlRows,
            state.source_request,
            state.type_chart,
            { otherLabel: this.$t('ReportBuilder.sqlTab.otherCategory') }
          );
        } else {
          const data = await fetchReportData(built.payload, { reportUuid: this.activeReportId || null, signal });
          state.compareData = unwrapReportData(data).rows;
        }
        state.comparePeriod = { from: built.period.from, to: built.period.to, shift: built.period.shift };
      } catch (e) {
        if (e?.aborted) return; // superseded — the newer call owns compareLoading now
        console.error('Failed to load compare data:', e);
        clear();
      } finally {
        if (this._compareAbortController === controller) state.compareLoading = false;
      }
    },
    // В неизолированном режиме (конструктор, страница отчёта) чарт и таблицы читают
    // стор, а не локальное состояние — переносим туда результат сравнения.
    syncCompareToStore() {
      if (this.isolated) return;
      const state = this.localReportState;
      if (!state.comparePeriod) {
        this.$store.commit('ReportFromBuilder/clearCompareData');
        return;
      }
      if (this.isSqlReport) {
        this.$store.commit('ReportFromBuilder/setCompareSqlResult', {
          response: state.compareSqlRows,
          period: state.comparePeriod
        });
      } else {
        this.$store.commit('ReportFromBuilder/setCompareData', {
          data: state.compareData || [],
          period: state.comparePeriod
        });
      }
    },
    // isFallbackAttempt guards against recursing again if fallbackReportId is itself
    // (mis)configured as tariff-gated — we only ever degrade one level.
    loadReport: async function(idOverride = null, isFallbackAttempt = false) {
      // A re-load (param change, dashboard filter, etc.) or unmount supersedes whatever
      // this widget already has in flight — abort it instead of letting it race the new
      // one for a connection/PHP-FPM worker (this is what made switching dashboards while
      // one was still loading feel stuck: the old widgets' requests kept the new
      // dashboard's own request queued behind them).
      if (!isFallbackAttempt && this._mainAbortController) this._mainAbortController.abort();
      const controller = isFallbackAttempt ? this._mainAbortController : new AbortController();
      if (!isFallbackAttempt) this._mainAbortController = controller;
      const signal = controller.signal;

      this.localLoading = true;
      this.missingParams = [];
      this.premiumRequired = false;
      const reportUuid = idOverride || this.report_id || null;
      this.activeReportId = reportUuid;
      try {
        let template = null;
        let isCustomQuery = false;

        if (!isFallbackAttempt && this.reportData) {
          template = this.reportData;
          this.reportInfo = this.reportData;
          isCustomQuery = !!this.reportData.has_custom_query;
        } else if (reportUuid) {
          // A tariff-forbidden report is a normal per-widget outcome, not a reason to
          // yank the whole app to Home — suppress that and show an in-place stub instead.
          const report = await userReportService.view(reportUuid, { suppressAccessDenied: true, signal });
          this.reportInfo = report;
          isCustomQuery = !!report?.has_custom_query;
          if (report && report.report_template) {
            template = report.report_template;
          }
        }

        if (template) {
           this.syncToLocalState(template);

           // A pinned widget date range overrides the dashboard's global one entirely;
           // otherwise re-apply the global range if set.
           if (this.widgetDateRangeOverride) {
               if (!this.localReportState.settings) this.localReportState.settings = { params: {} };
               if (!this.localReportState.settings.params) this.localReportState.settings.params = {};
               this.localReportState.settings.params[':date_from'] = this.widgetDateRangeOverride;
               this.localReportState.settings.params[':date_to'] = 'now';
           } else if (this.globalDateRange && (this.globalDateRange.from || this.globalDateRange.to)) {
               if (!this.localReportState.settings) this.localReportState.settings = { params: {} };
               if (!this.localReportState.settings.params) this.localReportState.settings.params = {};
               this.localReportState.settings.params[':date_from'] = this.globalDateRange.from;
               this.localReportState.settings.params[':date_to'] = this.globalDateRange.to;
           }
           if (this.globalGranularity) {
               if (!this.localReportState.source_request) this.localReportState.source_request = {};
               this.localReportState.source_request.granularity = this.globalGranularity;
           }
           // После syncToLocalState: он только что перезаписал visual/type_chart из шаблона отчёта
           this.applyGlobalCompare();
           this.applyWidgetVisualOverride();
           this.applyWidgetTypeChartOverride();
           this.applySegmentOverride();

           if (!this.isolated) {
             this.$store.dispatch('ReportFromBuilder/importPayload', template);
             // Tell the store whether this report uses a custom query and its UUID,
             // so loadDataForReport (triggered by param/filter changes) routes correctly.
             this.$store.commit('ReportFromBuilder/setCustomQuery', { uuid: reportUuid, isCustom: isCustomQuery });
           }
        }

        // Template reports declare required_params (`:param` placeholders in event_id);
        // executing without them would just 400 with "Param not found" — show a stub instead.
        // Params arrive via extraParams (dashboard widgets / ?params= deep links).
        const required = this.reportInfo?.required_params || [];
        const provided = this.extraParams || {};
        this.missingParams = required.filter(p => !Object.prototype.hasOwnProperty.call(provided, ':' + p));
        if (this.missingParams.length) {
          this.$emit('loaded', this.reportInfo);
          return;
        }

        let data = null;

        // SQL-отчёт: свой эндпоинт, график собирается из строк на клиенте.
        // Серверное исполнение сохранённого SQL (preview/MCP) — отдельная задача.
        if (this.isSqlReport) {
            const sqlPayload = this.buildExecutePayload();
            const response = await fetchSqlReportData(sqlPayload, { reportUuid, signal });
            if (response !== null) {
                applySqlResult(this.localReportState, response, {
                    otherLabel: this.$t('ReportBuilder.sqlTab.otherCategory')
                });
                if (!this.isolated) {
                    this.$store.commit('ReportFromBuilder/setSqlResult', response);
                }
                // Только после applySqlResult: он мог автоподобрать маппинг,
                // в который строки сравнения обязаны лечь
                this.loadCompareData(sqlPayload).then(() => this.syncCompareToStore());
            }
            this.$emit('loaded', this.reportInfo);
            return;
        }

        const payload = this.buildExecutePayload();

        if (isCustomQuery && reportUuid) {
            // Custom query: call user-report/execute with current settings
            data = await fetchReportData(payload, { isCustomQuery: true, reportUuid, signal });
        } else {
            // Standard template path
            data = await fetchReportData(payload, { reportUuid, signal });
        }

        if (data !== null) {
            // Set chart data locally
            const unwrapped = unwrapReportData(data);
            this.localReportState.chartData = unwrapped.rows;
            if (unwrapped.wrapped) this.localReportState.total_count = unwrapped.total_count;

            // Sync to Vuex if not isolated
            if (!this.isolated) {
                this.$store.commit('ReportFromBuilder/setChartData', data);
            }
        }

        // Сравнение — вдогонку, основной график не ждёт второй ответ
        if (!isCustomQuery) {
            this.loadCompareData(payload).then(() => this.syncCompareToStore());
        }

        this.$emit('loaded', this.reportInfo);
      } catch (e) {
        if (e?.aborted) return; // superseded by a newer load — the new call owns loading/state now
        if (e?.server?.answer === 403) {
          if (!isFallbackAttempt && this.fallbackReportId) {
            await this.loadReport(this.fallbackReportId, true);
            return;
          }
          this.premiumRequired = true;
          this.$emit('loaded', this.reportInfo);
        } else {
          console.error("Failed to load report:", e);
        }
      } finally {
        if (this._mainAbortController === controller) this.localLoading = false;
      }
    }
  },
  created() {
    this._mainAbortController = null;
    this._compareAbortController = null;
  },
  mounted() {
    this.loadReport()
  },
  unmounted() {
    // Dashboard switch removes this widget from the layout before its requests finish —
    // abort them so they stop competing with the new dashboard's own requests.
    if (this._mainAbortController) this._mainAbortController.abort();
    if (this._compareAbortController) this._compareAbortController.abort();
  },
  expose: ['loadReport']
}
</script>

<style scoped>
.report-container {
  height: 100%;
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.loading-placeholder {
  flex-grow: 1;
  height: 100%;
  min-height: 200px;
}

.upgrade-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  margin-bottom: 8px;
  flex-shrink: 0;
  background: var(--c-gold-bg, #FFF4DC);
  color: var(--c-gold-fg, #B85A0B);
  border: 1px solid var(--c-gold-bd, #FDE68A);
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.4;
}

.upgrade-banner-icon {
  font-size: 12px;
  flex-shrink: 0;
}

.upgrade-banner-text {
  flex-grow: 1;
}

.upgrade-banner-link {
  font-weight: 600;
  white-space: nowrap;
  color: var(--c-gold-fg, #B85A0B);
  text-decoration: underline;
}

.report-config-error {
  flex-grow: 1;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px 16px;
  color: var(--el-color-danger, #f56c6c);
}

.report-config-error-icon {
  font-size: 32px;
  margin-bottom: 12px;
}

.report-config-error-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--ui-text-1, #303133);
  margin-bottom: 8px;
}

.report-config-error-list {
  margin: 0;
  padding: 0;
  list-style: none;
  color: var(--ui-text-2, #606266);
  font-size: 13px;
}

.report-config-error-list li {
  margin-bottom: 4px;
}

.premium-icon {
  color: var(--c-gold-fg, #fba018);
}

.btn-premium-inline {
  background: var(--c-gold-bg, #FFF4DC);
  color: var(--c-gold-fg, #B85A0B);
  border: 1px solid var(--c-gold-bd, #FDE68A);
  border-radius: 8px;
  padding: 6px 16px;
  font-weight: 600;
  text-decoration: none;
}

.btn-premium-inline:hover {
  filter: brightness(0.95);
  color: var(--c-gold-fg, #B85A0B);
}
</style>

<style>
.report-panel { background-color: #fff; }
.dark-version .report-panel { background-color: transparent; }
</style>